"use client";

import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { buttonVariants } from "@/components/ui/button";

type CompanyRow = {
  id: number;
  name: string;
  gstin?: string | null;
  phone?: string | null;
  email?: string | null;
  is_active?: number;
  created_at?: string | null;
  users_count?: number | string;
  sales_count?: number | string;
};

export default function ProductAdminCompaniesPage() {
  const { data = [], isLoading, isError } = useQuery({
    queryKey: ["product-admin", "companies"],
    queryFn: async () => {
      const { data } = await api.get<{ companies: CompanyRow[] }>("/api/product-admin/companies");
      return data.companies;
    },
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading companies…</p>;
  }
  if (isError) {
    return <p className="text-sm text-destructive">Could not load companies.</p>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Companies</h1>
        <p className="text-sm text-muted-foreground">All company setups (product admin only).</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-3">ID</th>
                  <th className="py-2 pr-3">Company</th>
                  <th className="py-2 pr-3">GSTIN</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Phone</th>
                  <th className="py-2 pr-3">Sales</th>
                  <th className="py-2 pr-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.map((c) => (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="py-2 pr-3">{c.id}</td>
                    <td className="py-2 pr-3 font-medium">
                      {c.name}
                    </td>
                    <td className="py-2 pr-3">{c.gstin ?? ""}</td>
                    <td className="py-2 pr-3">{c.email ?? ""}</td>
                    <td className="py-2 pr-3">{c.phone ?? ""}</td>
                    <td className="py-2 pr-3">{Number(c.sales_count ?? 0)}</td>
                    <td className="py-2 pr-3 text-right">
                      <Link
                        to={`/product-admin/companies/${c.id}`}
                        className={buttonVariants({ variant: "outline", size: "sm" })}
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

